import { FocusEventHandler, KeyboardEventHandler } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { DATE_FORMAT, parseDate } from 'constants/formats';
import dayjs from 'dayjs';

import { IDatePickerControlProps } from './types';
import { DatePicker } from '../DatePicker';

const MIN_DATE = dayjs('1900-01-01');
const MAX_DATE = dayjs('2099-01-01');

export const DatePickerControl = <T extends FieldValues>(props: UseControllerProps<T> & IDatePickerControlProps) => {
  const {
    name,
    dateFormat = DATE_FORMAT,
    control,
    rules,
    defaultValue,
    onChange,
    maxDate = MAX_DATE,
    minDate = MIN_DATE,
    ...restProps
  } = props;

  const { field, fieldState } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const { ref, value, onChange: onFieldChange, ...restField } = field;
  const { error } = fieldState;

  const changeHandler = (
    value?: dayjs.Dayjs | null,
    context?: PickerChangeHandlerContext<DateValidationError>,
  ): void => {
    onFieldChange(value);
    if (onChange && value && context) onChange(value, context);
  };

  const handleOnKeyUp: KeyboardEventHandler<HTMLInputElement> = (event) => {
    const value = (event.target as HTMLInputElement).value;
    const {
      date: [day, month, year],
      formats: [dayFormat, monthFormat, yearFormat],
    } = parseDate(value, dateFormat);

    const currentYear = dayjs(year, yearFormat).year();
    const currentMonth = dayjs(month, monthFormat).month();
    const currentDay = dayjs(day, dayFormat).date();

    const maxYear = maxDate?.year();
    const minYear = minDate?.year();

    const maxMonth = maxDate?.month();
    const minMonth = minDate?.month();

    const maxDay = maxDate?.date();
    const minDay = minDate?.date();

    const isWrongMaxYear = maxYear && currentYear > maxYear;
    const isWrongMaxMonth = maxMonth && currentYear === maxYear && currentMonth > maxMonth;
    const isWrongMaxDay = maxDay && currentYear === maxYear && currentMonth === maxMonth && currentDay > maxDay;

    const isWrongMinYear = currentYear.toString().length === 4 && currentYear < minYear;
    const isWrongMinMonth = currentYear === minYear && currentMonth < minMonth;
    const isWrongMinDay = currentYear === minYear && currentMonth === minMonth && currentDay < minDay;

    if (!minDate || !maxDate) {
      return;
    }

    if (isWrongMaxYear || isWrongMaxMonth || isWrongMaxDay) {
      changeHandler(maxDate);
      return;
    }

    if (isWrongMinYear || isWrongMinMonth || isWrongMinDay) {
      changeHandler(minDate);
      return;
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key.length === 1 && event.key.match(/[a-z]/i)) {
      event.preventDefault();
    }
  };

  const handleOnBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const value = (event.target as HTMLInputElement).value;
    const {
      date: [day, month, year],
      formats: [dayFormat, monthFormat, yearFormat],
    } = parseDate(value, dateFormat);

    const currentYear = dayjs(year, yearFormat).year();
    const currentMonth = dayjs(month, monthFormat).month();
    const currentDay = dayjs(day, dayFormat).date();

    const isDateValid =
      value.includes(currentYear.toString()) &&
      currentYear.toString().length === 4 &&
      [currentYear, currentMonth, currentDay].every((part) => !isNaN(part));

    if (isDateValid) {
      return;
    }
    changeHandler(null);
  };
  return (
    <DatePicker
      slotProps={{
        textField: {
          onKeyUp: handleOnKeyUp,
          onKeyDown: handleKeyDown,
          onBlur: handleOnBlur,
        },
      }}
      dateFormat={dateFormat}
      maxDate={maxDate}
      minDate={minDate}
      inputRef={ref}
      fieldError={error}
      value={value ? dayjs(value ?? '') : null}
      onChange={changeHandler}
      {...restProps}
      {...restField}
    />
  );
};
